import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { imageUrl } from '../lib/images';

export default function Forside() {
  const [projects, setProjects] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTestimonialIndex, setActiveTestimonialIndex] = useState(0);

  useEffect(() => {
    if (testimonials.length <= 1) return;
    const interval = setInterval(() => {
      setActiveTestimonialIndex((prev) => (prev + 1) % testimonials.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [testimonials]);

  useEffect(() => {
    async function fetchData() {
      try {
        const [projectsRes, testimonialsRes] = await Promise.all([
          fetch('https://api.dev.auxo.dk/public/v2/content/projects?limit=3'),
          fetch('https://api.dev.auxo.dk/public/v2/content/testimonials')
        ]);

        if (projectsRes.ok) {
          const data = await projectsRes.json();
          setProjects(data.items || []);
        }
        if (testimonialsRes.ok) {
          const data = await testimonialsRes.json();
          setTestimonials(data.items || []);
        }
      } catch (error) {
        console.error("Fejl ved hentning af CMS-data:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  const coreServices = [
    {
      title: 'Nyt tag & Tagrenovering',
      desc: 'Alt fra mindre tagreparationer til komplet udskiftning af tegltag, betontag eller eternittag. Vi rådgiver om de bedste materialer.',
      icon: 'fa-solid fa-house-chimney-crack',
      link: '/ydelser/nyt-tag',
      image: '/cdn-cgi/image/width=600,quality=85,format=auto/_assets/8116.jpg'
    },
    {
      title: 'Eksklusive Træterrasser',
      desc: 'Skræddersyede træterrasser i lærk, hårdttræ eller komposit. Vi designer og bygger efter dine drømme og havens muligheder.',
      icon: 'fa-solid fa-tree',
      link: '/ydelser/terrasser',
      image: 'https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/2c036395-f6c9-48ce-982b-c0c810ba745b/wooden-terrace-danish.jpg'
    },
    {
      title: 'Tilbygninger & Renovering',
      desc: 'Få mere plads med en flot tilbygning, eller lad os stå for modernisering, totalrenovering og energiforbedringer af din bolig.',
      icon: 'fa-solid fa-trowel-bricks',
      link: '/ydelser/tilbygninger-renovering',
      image: 'https://assets.auxodev.dk/assets/2c036395-f6c9-48ce-982b-c0c810ba745b/8131.jpg'
    },
    {
      title: 'Døre & Vinduer',
      desc: 'Udskiftning og montering af energirigtige døre og vinduer i højeste kvalitet. Vi sikrer perfekt tætning og flot finish.',
      icon: 'fa-solid fa-door-open',
      link: '/ydelser/doere-vinduer',
      image: 'https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/2c036395-f6c9-48ce-982b-c0c810ba745b/windows-doors-danish.jpg'
    }
  ];

  const pillars = [
    {
      title: 'Høj kvalitet',
      desc: 'Vi går aldrig på kompromis med materialer eller udførelse. Godt håndværk holder i generationer.',
      icon: 'fa-solid fa-award'
    },
    {
      title: 'Ordentlig dialog',
      desc: 'Vi lytter til dine ønsker, forklarer processen og holder dig opdateret under hele forløbet.',
      icon: 'fa-solid fa-comments'
    },
    {
      title: 'Overholdte aftaler',
      desc: 'En aftale er en aftale. Vi kommer til tiden, overholder tidsplanen og leverer til den aftalte pris.',
      icon: 'fa-solid fa-calendar-check'
    },
    {
      title: 'Lokal forankring',
      desc: 'Som din lokale tømrermester i Vester Nebel værner vi om vores gode ry og den personlige relation.',
      icon: 'fa-solid fa-location-dot'
    }
  ];

  return (
    <>
      <SEO 
        title="Forside" 
        description="Tømrermester Mikkel Juler ApS i Egtved tilbyder professionelt tømrerarbejde i høj kvalitet. Vi udfører nyt tag, træterrasser, tilbygninger, renovering samt døre og vinduer." 
      />

      {/* Hero Section */}
      <section className="relative bg-brand-cream py-12 md:py-20 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Tekst indhold */}
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 bg-brand-green-light text-brand-green px-3.5 py-1.5 rounded-full text-xs sm:text-sm font-semibold">
                <i className="fa-solid fa-circle-check" />
                <span>Din lokale tømrermester</span>
              </div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-brand-dark tracking-tight leading-tight">
                Personligt tømrerarbejde i <span className="text-brand-green">dit lokalområde</span>
              </h1>
              <p className="text-base sm:text-lg text-brand-dark-light max-w-xl leading-relaxed">
                Søger du en tømrermester, der overholder sine aftaler, leverer kompromisløst håndværk, går op i ordentlighed og vægter den gode, personlige dialog? Hos Tømrermester Mikkel Juler ApS hjælper vi private boligejere og erhvervskunder i Egtved, Kolding, Vejle og omegn med alt fra nye tage til skræddersyede træterrasser.
              </p>
              <div className="flex flex-wrap gap-4 pt-2">
                <Link 
                  to="/kontakt" 
                  className="bg-brand-green hover:bg-brand-green/90 text-white font-semibold px-6 py-3.5 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 cursor-pointer text-sm sm:text-base"
                >
                  Få et uforpligtende tilbud
                </Link>
                <a 
                  href="tel:+4528455012" 
                  className="bg-white hover:bg-gray-50 text-brand-dark border border-gray-200 font-semibold px-6 py-3.5 rounded-xl shadow-xs transition-all duration-200 flex items-center gap-2 text-sm sm:text-base"
                >
                  <i className="fa-solid fa-phone text-brand-wood" />
                  <span>Ring i dag</span>
                </a>
              </div>
            </div>

            {/* Hero Billede */}
            <div className="relative">
              <div className="absolute -inset-2 bg-brand-wood/10 rounded-3xl blur-xl" />
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-gray-100">
                <img 
                  src="https://assets.auxodev.dk/assets/2c036395-f6c9-48ce-982b-c0c810ba745b/8135.jpg" 
                  alt="Tømrermester Mikkel Juler i gang med tømrerarbejde på en moderne dansk bolig" 
                  className="w-full h-[360px] sm:h-[460px] lg:h-[480px] object-cover"
                />
                <div className="absolute bottom-4 left-4 right-4 bg-white/90 backdrop-blur-xs p-4 rounded-xl border border-white/20 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-brand-green flex items-center justify-center text-white">
                    <i className="fa-solid fa-hammer" />
                  </div>
                  <div>
                    <span className="block text-xs text-gray-500 uppercase tracking-wider font-semibold">Udført arbejde</span>
                    <span className="block text-sm font-bold text-brand-dark">Koldingvej 49B, Egtved</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Kort præsentation */}
      <section className="bg-white py-16 border-t border-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center space-y-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-brand-dark">
            Velkommen til Tømrermester Mikkel Juler ApS
          </h2>
          <p className="text-base sm:text-lg text-brand-dark-light leading-relaxed">
            Hos os er det det gode håndværk og den personlige relation, der er i centrum. Vi mener, at de bedste resultater skabes gennem en tæt og ærlig dialog med dig som kunde. Uanset om du skal have skiftet et vindue, bygget en ny træterrasse eller står overfor en større totalrenovering, går vi til opgaven med samme faglige stolthed og engagement. Vi udfører opgaver for både private boligejere og erhvervsvirksomheder.
          </p>
          <div className="inline-block">
            <Link to="/om-mikkel" className="text-brand-green hover:text-brand-green/80 font-semibold flex items-center gap-2 group cursor-pointer">
              <span>Læs mere om Mikkel og vores værdier</span>
              <i className="fa-solid fa-arrow-right transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* Derfor vælger kunder os */}
      <section className="bg-gray-50 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <span className="text-brand-wood font-semibold text-sm uppercase tracking-wider block">Vores fundament</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-brand-dark">Derfor vælger kunder os</h2>
            <p className="text-brand-dark-light">
              Vi bygger vores forretning på tilfredse kunder. Her er de fire grundpiller, som sikrer dig en tryg og succesfuld byggeproces.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {pillars.map((pillar, idx) => (
              <div key={idx} className="bg-white p-8 rounded-2xl border border-gray-100 shadow-xs hover:shadow-md transition-shadow duration-300">
                <div className="w-12 h-12 rounded-xl bg-brand-green-light text-brand-green flex items-center justify-center text-xl mb-6">
                  <i className={pillar.icon} />
                </div>
                <h3 className="text-lg font-bold text-brand-dark mb-3">{pillar.title}</h3>
                <p className="text-brand-dark-light text-sm leading-relaxed">{pillar.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Kerneydelser */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <span className="text-brand-green font-semibold text-sm uppercase tracking-wider block">Hvad tilbyder vi?</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-brand-dark">Vores fire kerneydelser</h2>
            <p className="text-brand-dark-light">
              Vi dækker alle aspekter af tømrerfaget. Klik dig ind på de enkelte ydelser for at læse mere om vores løsninger og se billeder af vores arbejde.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {coreServices.map((service, idx) => (
              <Link 
                key={idx} 
                to={service.link} 
                className="group bg-brand-cream rounded-2xl overflow-hidden border border-gray-100 shadow-xs hover:shadow-lg hover:border-brand-green/20 transition-all duration-300 flex flex-col sm:flex-row md:flex-col cursor-pointer"
              >
                <div className="sm:w-1/2 md:w-full relative overflow-hidden h-48 sm:h-auto md:h-56">
                  <img 
                    src={service.image} 
                    alt={service.title} 
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="sm:w-1/2 md:w-full p-6 sm:p-8 flex flex-col justify-between space-y-4 flex-1">
                  <div className="space-y-2">
                    <h3 className="text-lg font-bold text-brand-dark group-hover:text-brand-green transition-colors duration-200">{service.title}</h3>
                    <p className="text-brand-dark-light text-sm leading-relaxed">{service.desc}</p>
                  </div>
                  <div>
                    <div className="inline-flex items-center gap-2 text-brand-green font-semibold text-sm group-hover:text-brand-green/80">
                      <span>Læs mere</span>
                      <i className="fa-solid fa-arrow-right text-xs transition-transform duration-200 group-hover:translate-x-1" />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Sektion om personlig tilgang */}
      <section className="bg-brand-green text-white py-16 md:py-24 overflow-hidden relative">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute top-10 left-10 w-72 h-72 rounded-full border-4 border-white" />
          <div className="absolute bottom-10 right-10 w-96 h-96 rounded-full border-4 border-white" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5">
              <img 
                src="https://assets.auxodev.dk/assets/2c036395-f6c9-48ce-982b-c0c810ba745b/6954_67868d44.jpg" 
                alt="Tømrermester Mikkel Juler" 
                className="rounded-2xl shadow-2xl object-cover w-full h-[400px]"
              />
            </div>
            <div className="lg:col-span-7 space-y-6">
              <span className="text-brand-wood font-semibold text-sm uppercase tracking-wider block">Den personlige relation</span>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">
                En tømrermester du kan tale med – og stole på
              </h2>
              <p className="text-gray-200 leading-relaxed">
                Når du vælger Tømrermester Mikkel Juler ApS, vælger du ikke bare et tømrerfirma – du vælger en samarbejdspartner. Jeg tror på, at godt byggeri starter med en god snak. Derfor er jeg med hele vejen: lige fra den første opmåling og rådgivning om materialer, til det sidste fejeblad er brugt, og projektet afleveres.
              </p>
              <p className="text-gray-200 leading-relaxed">
                For mig er det afgørende, at du føler dig tryg. Det betyder, at jeg altid overholder mine aftaler, rydder ordentligt op efter mig selv hver dag, og at der ikke dukker ubehagelige overraskelser op på regningen. Det er sådan, vi opbygger langvarige relationer til vores kunder i lokalområdet.
              </p>
              <div className="pt-4 flex flex-col sm:flex-row gap-6 items-start sm:items-center">
                <div>
                  <span className="block text-2xl font-bold text-brand-wood">Mikkel Juler</span>
                  <span className="block text-xs text-gray-300 uppercase tracking-widest font-semibold">Ejer & Tømrermester</span>
                </div>
                <div className="hidden sm:block h-8 w-px bg-white/20" />
                <div className="flex items-center gap-3">
                  <i className="fa-solid fa-phone text-brand-wood text-xl" />
                  <span className="font-semibold text-lg">28 45 50 12</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Udvalgte projekter */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12 gap-4">
            <div>
              <span className="text-brand-green font-semibold text-sm uppercase tracking-wider block">Vores arbejde</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-brand-dark mt-2">Udvalgte projekter</h2>
            </div>
            <div>
              <Link 
                to="/projekter" 
                className="bg-brand-wood-light hover:bg-brand-wood/20 text-brand-wood-dark font-semibold px-5 py-2.5 rounded-xl transition-all duration-200 flex items-center gap-2 text-sm cursor-pointer"
              >
                <span>Se alle projekter</span>
                <i className="fa-solid fa-arrow-right" />
              </Link>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-10 h-10 border-4 border-brand-green border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {projects.map((project) => (
                <div key={project.id} className="bg-brand-cream rounded-2xl overflow-hidden border border-gray-100 shadow-xs hover:shadow-md transition-all duration-300 flex flex-col h-full">
                  <div className="h-56 overflow-hidden relative">
                    <img 
                      src={imageUrl(project.image?.[0], 'width=500,quality=80,format=auto')} 
                      alt={project.title?.da} 
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-4 left-4 bg-brand-green text-white text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider">
                      {project.category === 'nyt-tag' && 'Nyt tag'}
                      {project.category === 'terrasser' && 'Terrasse'}
                      {project.category === 'tilbygninger' && 'Tilbygning'}
                      {project.category === 'doere-vinduer' && 'Døre & vinduer'}
                    </div>
                  </div>
                  <div className="p-6 flex flex-col justify-between flex-grow space-y-4">
                    <div>
                      <h3 className="text-lg font-bold text-brand-dark mb-2">{project.title?.da}</h3>
                      <div 
                        className="text-brand-dark-light text-sm line-clamp-3 leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: project.description?.da }}
                      />
                    </div>
                    {project.completion_date && (
                      <div className="text-xs text-gray-400 font-medium pt-2 border-t border-gray-100">
                        Udført: {new Date(project.completion_date).toLocaleDateString('da-DK', { month: 'long', year: 'numeric' })}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Kundeanmeldelser som Slideshow */}
      <section className="bg-gray-50 py-16 md:py-24 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto space-y-4 mb-12">
            <span className="text-brand-wood font-semibold text-sm uppercase tracking-wider block">Kundeanmeldelser</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-brand-dark">Det siger vores kunder</h2>
            <p className="text-brand-dark-light">
              Kundernes tilfredshed er vores største motivation. Her er et udpluk af de tilbagemeldinger, vi får fra glade boligejere og erhvervsdrivende.
            </p>
          </div>

          {testimonials.length > 0 ? (
            <div className="max-w-4xl mx-auto relative px-4 sm:px-12">
              {/* Slideshow Card */}
              <div className="relative bg-white p-8 sm:p-12 rounded-3xl border border-gray-100 shadow-lg overflow-hidden min-h-[280px] sm:min-h-[240px] flex flex-col justify-between transition-all duration-500 ease-in-out">
                {/* Stor Citat-ikon i baggrunden */}
                <div className="absolute right-6 top-6 text-brand-green/5 text-7xl sm:text-8xl pointer-events-none font-serif select-none">
                  ”
                </div>

                <div className="space-y-6 relative z-10">
                  {/* Stjerner */}
                  <div className="flex text-amber-400 gap-1">
                    {[...Array(testimonials[activeTestimonialIndex].rating || 5)].map((_, i) => (
                      <i key={i} className="fa-solid fa-star text-base sm:text-lg" />
                    ))}
                  </div>

                  {/* Kommentar */}
                  <p className="text-brand-dark text-base sm:text-xl italic leading-relaxed font-medium transition-opacity duration-300">
                    "{testimonials[activeTestimonialIndex].comment?.da}"
                  </p>
                </div>

                {/* Forfatter & Lokation */}
                <div className="flex flex-wrap justify-between items-center pt-6 mt-6 border-t border-gray-50 relative z-10 gap-4">
                  <div>
                    <span className="font-bold text-brand-dark text-base sm:text-lg block">
                      {testimonials[activeTestimonialIndex].name}
                    </span>
                    {testimonials[activeTestimonialIndex].location && (
                      <span className="text-xs text-brand-wood font-semibold uppercase tracking-wider flex items-center gap-1.5 mt-1">
                        <i className="fa-solid fa-location-dot" />
                        {testimonials[activeTestimonialIndex].location}
                      </span>
                    )}
                  </div>
                  
                  {/* Manuel navigation (pile) */}
                  <div className="flex gap-2">
                    <button 
                      onClick={() => setActiveTestimonialIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length)}
                      className="w-10 h-10 rounded-xl bg-gray-50 hover:bg-brand-green hover:text-white text-brand-dark transition-all duration-200 flex items-center justify-center cursor-pointer shadow-xs border border-gray-100"
                      aria-label="Forrige anmeldelse"
                    >
                      <i className="fa-solid fa-chevron-left text-sm" />
                    </button>
                    <button 
                      onClick={() => setActiveTestimonialIndex((prev) => (prev + 1) % testimonials.length)}
                      className="w-10 h-10 rounded-xl bg-gray-50 hover:bg-brand-green hover:text-white text-brand-dark transition-all duration-200 flex items-center justify-center cursor-pointer shadow-xs border border-gray-100"
                      aria-label="Næste anmeldelse"
                    >
                      <i className="fa-solid fa-chevron-right text-sm" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Indikator-prikker (dots) */}
              <div className="flex justify-center gap-2.5 mt-6">
                {testimonials.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveTestimonialIndex(idx)}
                    className={`h-2.5 rounded-full transition-all duration-300 cursor-pointer ${
                      idx === activeTestimonialIndex 
                        ? 'w-8 bg-brand-green' 
                        : 'w-2.5 bg-gray-300 hover:bg-gray-400'
                    }`}
                    aria-label={`Gå til anmeldelse ${idx + 1}`}
                  />
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-10 h-10 border-4 border-brand-green border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          )}
        </div>
      </section>

      {/* Kontaktinformation Sektion - Restyled to match Design Reference */}
      <section className="relative py-24 md:py-32 overflow-hidden bg-brand-dark">
        {/* Background Image with Dark Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src={imageUrl("https://assets.auxodev.dk/assets/2c036395-f6c9-48ce-982b-c0c810ba745b/7566.jpg", "width=1600,quality=80,format=auto")} 
            alt="Tømrerarbejde i høj kvalitet" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-brand-dark/85 mix-blend-multiply" />
        </div>

        {/* Centrally Aligned, Single-Column Composition */}
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center flex flex-col items-center justify-center space-y-6">
          {/* Large Primary Heading (Display Serif) */}
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-white tracking-tight leading-tight">
            Skal vi realisere dit næste byggeprojekt?
          </h2>

          {/* Brief, slightly smaller subheading (Clean Sans-serif) */}
          <p className="text-gray-200 text-base sm:text-lg md:text-xl max-w-2xl leading-relaxed font-sans">
            Tømrermester Mikkel Juler ApS leverer solidt håndværk og personlig service i Vester Nebel og omegn. Ring til os på <span className="whitespace-nowrap">28 45 50 12</span> eller send en mail til <span className="whitespace-nowrap">Mikkeljuler@gmail.com</span> for en uforpligtende snak.
          </p>

          {/* CTA Button (Solid, contrasting surface block with sharp corners, label text utilizes accent/CTA color) */}
          <div className="pt-4">
            <Link 
              to="/kontakt" 
              className="inline-block bg-white hover:bg-gray-100 text-brand-green font-bold tracking-wider uppercase px-8 py-4 rounded-none transition-all duration-200 shadow-lg text-sm sm:text-base cursor-pointer"
            >
              Få et uforpligtende tilbud
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
